import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Bed, Bath, Maximize, Calendar, MapPin, Heart, Share2, Sparkles, ArrowLeft, Building, Thermometer, Car, DollarSign, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/propertyai/components/ui/button';
import { Card } from '@/propertyai/components/ui/card';
import { Badge } from '@/propertyai/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/propertyai/components/ui/tabs';
import { Separator } from '@/propertyai/components/ui/separator';
import { Sheet, SheetContent } from '@/propertyai/components/ui/sheet';
import AVMWidget from '@/propertyai/components/AVMWidget';
import MortgageCalc from '@/propertyai/components/MortgageCalc';
import AIChat from '@/propertyai/components/AIChat';
import MapView from '@/propertyai/components/MapView';
import PropertyCard from '@/propertyai/components/PropertyCard';
import { getProperty } from '@/propertyai/lib/api';
import { toast } from 'sonner';

export default function PropertyDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    loadProperty();
  }, [id]);

  const loadProperty = async () => {
    setLoading(true);
    try {
      const res = await getProperty(id);
      setData(res.data);
    } catch (e) {
      console.error('Failed to load property', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="aspect-[2/1] shimmer rounded-2xl mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
          <div className="space-y-4">
            <div className="h-8 shimmer rounded w-1/2" />
            <div className="h-6 shimmer rounded w-3/4" />
            <div className="h-40 shimmer rounded-2xl" />
          </div>
          <div className="space-y-4">
            <div className="h-60 shimmer rounded-2xl" />
            <div className="h-60 shimmer rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 text-center">
        <h2 className="text-2xl font-semibold mb-4">Property Not Found</h2>
        <Link to="/property-ai/search"><Button className="rounded-xl">Back to Search</Button></Link>
      </div>
    );
  }

  const { property, agent, comparables, neighborhood, valuation } = data;
  const photos = property.photos || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Back + Actions */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <Link to="/property-ai/search" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to search
        </Link>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm" className="rounded-xl gap-1.5"
            onClick={() => { setSaved(!saved); toast(saved ? 'Removed from saved' : 'Saved to favorites'); }}
            data-testid="detail-save-button"
          >
            <Heart className={`w-4 h-4 ${saved ? 'fill-red-500 text-red-500' : ''}`} />
            {saved ? 'Saved' : 'Save'}
          </Button>
          <Button variant="outline" size="sm" className="rounded-xl gap-1.5" onClick={() => { navigator.clipboard.writeText(window.location.href); toast('Link copied!'); }}>
            <Share2 className="w-4 h-4" /> Share
          </Button>
          <Button
            size="sm" className="rounded-xl gap-1.5 bg-primary hover:bg-primary/90"
            onClick={() => setChatOpen(true)}
            data-testid="detail-ask-ai-button"
          >
            <Sparkles className="w-4 h-4" /> Ask AI
          </Button>
        </div>
      </div>

      {/* Photo Gallery */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-6">
        <div className="relative aspect-[2/1] sm:aspect-[2.5/1] rounded-2xl overflow-hidden bg-muted">
          <img
            src={photos[photoIdx] || 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800'}
            alt={property.address}
            className="w-full h-full object-cover"
          />
          {photos.length > 1 && (
            <>
              <button
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 text-[#000] flex items-center justify-center shadow-sm hover:bg-white"
                onClick={() => setPhotoIdx(prev => (prev - 1 + photos.length) % photos.length)}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 text-[#000] flex items-center justify-center shadow-sm hover:bg-white"
                onClick={() => setPhotoIdx(prev => (prev + 1) % photos.length)}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {photos.map((_, i) => (
                  <button
                    key={i}
                    className={`w-2 h-2 rounded-full transition-colors ${i === photoIdx ? 'bg-white' : 'bg-white/50'}`}
                    onClick={() => setPhotoIdx(i)}
                  />
                ))}
              </div>
            </>
          )}
          <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2.5 py-1 rounded-lg">
            {photoIdx + 1} / {photos.length}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
          {/* Main Content */}
          <div>
            {/* Title + Price */}
            <div className="mb-6">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-semibold font-['Space_Grotesk'] tabular-nums">
                    ${property.price?.toLocaleString()}
                  </h1>
                  <div className="flex items-center gap-1 text-muted-foreground mt-1">
                    <MapPin className="w-4 h-4" />
                    <span>{property.address}, {property.city}, {property.state} {property.zip}</span>
                  </div>
                </div>
                <Badge className={property.status === 'active' ? 'bg-green-500/90 text-white' : property.status === 'pending' ? 'bg-amber-500/90 text-white' : 'bg-red-500/90 text-white'}>
                  {property.status?.charAt(0).toUpperCase() + property.status?.slice(1)}
                </Badge>
              </div>

              {/* Key stats */}
              <div className="flex flex-wrap gap-4 mt-4">
                {[
                  { icon: Bed, label: `${property.beds} Beds` },
                  { icon: Bath, label: `${property.baths} Baths` },
                  { icon: Maximize, label: `${property.sqft?.toLocaleString()} sqft` },
                  { icon: Calendar, label: `Built ${property.year_built}` },
                  { icon: Building, label: property.property_type?.charAt(0).toUpperCase() + property.property_type?.slice(1) },
                  { icon: Clock, label: `${property.days_on_market}d on market` },
                ].map(({ icon: Icon, label }, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-sm">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            <Separator className="my-6" />

            {/* Description */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-3">About This Property</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{property.description}</p>
              {property.features && (
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {property.features.map((f, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Property Details */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-3">Property Details</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Lot Size', value: `${property.lot_size?.toLocaleString()} sqft` },
                  { label: 'Parking', value: property.parking },
                  { label: 'Heating', value: property.heating },
                  { label: 'Cooling', value: property.cooling },
                  { label: 'HOA', value: property.hoa ? `$${property.hoa}/mo` : 'None' },
                  { label: 'MLS #', value: property.mls_number },
                ].map(({ label, value }, i) => (
                  <div key={i} className="bg-secondary/50 rounded-xl p-3">
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-sm font-medium mt-0.5">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Price History */}
            {property.price_history && property.price_history.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold mb-3">Price History</h2>
                <div className="border border-border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Date</th>
                        <th className="text-left p-3 font-medium">Event</th>
                        <th className="text-right p-3 font-medium">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {property.price_history.slice(-5).reverse().map((h, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="p-3 text-muted-foreground">{h.date}</td>
                          <td className="p-3">
                            <Badge variant="secondary" className="text-xs">{h.event}</Badge>
                          </td>
                          <td className="p-3 text-right font-medium tabular-nums">${h.price?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Comparables */}
            {comparables && comparables.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold mb-3">Comparable Properties</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {comparables.map(comp => (
                    <PropertyCard key={comp.id} property={comp} compact />
                  ))}
                </div>
              </div>
            )}

            {/* Neighborhood */}
            {neighborhood && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold mb-3">Neighborhood: {neighborhood.name}</h2>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    { label: 'Walk Score', value: neighborhood.walk_score, color: 'text-green-600' },
                    { label: 'Transit Score', value: neighborhood.transit_score, color: 'text-blue-600' },
                    { label: 'Bike Score', value: neighborhood.bike_score, color: 'text-amber-600' },
                  ].map(({ label, value, color }, i) => (
                    <Card key={i} className="rounded-xl p-3 text-center border border-border">
                      <p className={`text-2xl font-semibold tabular-nums ${color}`}>{value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{label}</p>
                    </Card>
                  ))}
                </div>

                <Tabs defaultValue="schools">
                  <TabsList className="w-full">
                    <TabsTrigger value="schools" className="flex-1 text-xs">Schools</TabsTrigger>
                    <TabsTrigger value="demographics" className="flex-1 text-xs">Demographics</TabsTrigger>
                    <TabsTrigger value="safety" className="flex-1 text-xs">Safety</TabsTrigger>
                  </TabsList>
                  <TabsContent value="schools" className="mt-3 space-y-2">
                    {neighborhood.school_data?.map((school, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 bg-secondary/50 rounded-xl">
                        <div>
                          <p className="text-sm font-medium">{school.name}</p>
                          <p className="text-xs text-muted-foreground">{school.type} | {school.distance_miles} mi</p>
                        </div>
                        <Badge variant="secondary" className="text-xs">{school.rating}/10</Badge>
                      </div>
                    ))}
                  </TabsContent>
                  <TabsContent value="demographics" className="mt-3">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Population', value: neighborhood.demographics?.population?.toLocaleString() },
                        { label: 'Median Age', value: neighborhood.demographics?.median_age },
                        { label: 'Household Size', value: neighborhood.demographics?.household_size },
                        { label: 'Owner Occupied', value: `${neighborhood.demographics?.owner_occupied_pct}%` },
                        { label: 'Median Income', value: `$${neighborhood.median_income?.toLocaleString()}` },
                      ].map(({ label, value }, i) => (
                        <div key={i} className="bg-secondary/50 rounded-xl p-2.5">
                          <p className="text-xs text-muted-foreground">{label}</p>
                          <p className="text-sm font-medium mt-0.5">{value}</p>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                  <TabsContent value="safety" className="mt-3">
                    <div className="bg-secondary/50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm">Crime Index</span>
                        <span className={`font-semibold tabular-nums ${
                          neighborhood.crime_index <= 3 ? 'text-green-600' :
                          neighborhood.crime_index <= 6 ? 'text-amber-600' : 'text-red-600'
                        }`}>{neighborhood.crime_index}/10</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {neighborhood.crime_index <= 3 ? 'Low crime area - very safe neighborhood' :
                         neighborhood.crime_index <= 6 ? 'Moderate crime area - generally safe' :
                         'Higher crime area - exercise caution'}
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>

          {/* Right Rail */}
          <div className="space-y-4">
            {/* AVM Widget */}
            <AVMWidget valuation={valuation} priceHistory={property.price_history} />

            {/* Mortgage Calculator */}
            <MortgageCalc initialPrice={property.price} />

            {/* Agent Card */}
            {agent && (
              <Card className="rounded-2xl border border-border bg-background p-5">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">Listing Agent</h3>
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={agent.photo_url || `https://i.pravatar.cc/80?u=${agent.id}`}
                    alt={agent.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-medium text-sm">{agent.name}</p>
                    <p className="text-xs text-muted-foreground">{agent.brokerage}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-xs text-amber-500">{'*'.repeat(Math.round(agent.rating || 4))}</span>
                      <span className="text-xs text-muted-foreground">({agent.review_count} reviews)</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Button className="w-full rounded-xl" data-testid="contact-agent-button">Contact Agent</Button>
                  <Button variant="outline" className="w-full rounded-xl">Schedule Tour</Button>
                </div>
              </Card>
            )}

            {/* Mini Map */}
            {property.lat && property.lng && (
              <MapView
                properties={[property]}
                center={[property.lat, property.lng]}
                zoom={14}
                className="h-[200px]"
              />
            )}
          </div>
        </div>
      </div>

      {/* AI Chat Sheet */}
      <Sheet open={chatOpen} onOpenChange={setChatOpen}>
        <SheetContent className="w-[420px] sm:w-[480px] p-0">
          <AIChat isOpen={chatOpen} onClose={() => setChatOpen(false)} context={{ property_id: id }} />
        </SheetContent>
      </Sheet>
    </div>
  );
}

